import _ from 'underscore';
import React from 'react';
import getConfig from 'next/config';
import Joi from 'joi';

import { addCleanCache, parsePathParams } from '@culture/common-helpers/params';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addQuery, filterQuery } from '@culture/common-helpers/url';

import { publicationTypesHash } from '@culture/utils/helpers/publications';
import { statusesHash } from '@culture/utils/helpers/statuses';
import Template from 'pageTemplates/publications';

const { serverRuntimeConfig } = getConfig();

const PublicationsPage = (props) => <Template {...props} />;

const getPublications = ({
	urlParams,
	filtersQueryParams,
	rubricId,
	tagId,
	withPodcasts
}) => {
	const params = filterQuery({
		..._(filtersQueryParams).pick('query', 'sort', 'page'),
		fields: [
			'title',
			'name',
			'thumbnailFile',
			'shortText',
			'type',
			'podcasts'
		].join(),
		cached: true,
		statuses: ['published'],
		types: [publicationTypesHash.themes, publicationTypesHash.articles],
		rubrics: [rubricId],
		tags: [tagId],
		limit: 16,
		withPodcasts
	});

	return apiRequest(
		'publications',
		addCleanCache(params, urlParams.cleanCache)
	);
};

const getLatestNews = (cleanCache) => {
	const params = filterQuery({
		fields: ['title', 'name', 'thumbnailFile', 'publishDate', 'type'].join(),
		cached: true,
		statuses: ['published'],
		types: [publicationTypesHash.news],
		limit: 10
	});

	return apiRequest('publications', addCleanCache(params, cleanCache));
};

const getTagByName = (tagName) =>
	apiRequest('tags', { names: [tagName], statuses: [statusesHash.published] });

const getTags = ({ rubricPath, tagId, withPodcast }) => {
	const params = filterQuery({
		collectionName: 'publications',
		limit: 6,
		sort: '-count',
		statuses: [statusesHash.published],
		withPodcast,
		rubricPaths: [rubricPath],
		types: [publicationTypesHash.articles, publicationTypesHash.themes],
		tags: [tagId]
	});
	return apiRequest('tags/collection', params);
};

const getRubrics = ({ rubric, cleanCache }) =>
	apiRequest(
		'rubrics',
		addCleanCache(
			{
				paths: [rubric]
			},
			cleanCache
		)
	);

const getMeta = (page, cleanCache) => {
	const queryParams = addCleanCache(
		{
			rubricPath: 'publications',
			page,
			cached: true
		},
		cleanCache
	);

	return apiRequest('meta/catalog', queryParams);
};

const VALID_RUBRIC_PATHS = [
	'theaters',
	'museums',
	'cinema',
	'music',
	'architecture',
	'literature',
	'traditions',
	'lectures'
];

export async function getServerSideProps({ query, resolvedUrl }) {
	const defaultSort = '-publishDate';
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false),
		limit: Joi.number().default(24).min(1),
		query: Joi.string(),
		page: Joi.number().default(1).min(1).failover(1),
		pathParams: Joi.array().items(Joi.string()),
		sort: Joi.string()
			.valid('views', '-views', 'publishDate', '-publishDate')
			.failover(defaultSort)
			.default(defaultSort)
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const pathParamsValidationSchema = Joi.object().keys({
		rubric: Joi.string(),
		tag: Joi.string(),
		withPodcast: Joi.boolean()
	});

	const { rubric: parsedRubric, params: parsedPathParams } = parsePathParams(
		params.pathParams
	);

	const rubricPath = !_.isEmpty(parsedRubric) ? parsedRubric[0] : null;

	const pathParams = Joi.attempt(parsedPathParams, pathParamsValidationSchema, {
		stripUnknown: true
	});

	const { cleanCache, page } = params;
	const { tag: tagName, withPodcast: withPodcasts } = pathParams;

	const rubrics = rubricPath
		? await getRubrics({ rubric: rubricPath, cleanCache })
		: null;
	const rubric = !_.isEmpty(rubrics?.items) ? rubrics.items[0] : null;

	const selectedTags = tagName ? await getTagByName(tagName) : null;
	const tag = !_.isEmpty(selectedTags?.items) ? selectedTags.items[0] : null;
	if (rubricPath && !VALID_RUBRIC_PATHS.includes(rubricPath) && !rubric) {
		return {
			redirect: {
				destination: '/publications',
				permanent: false
			}
		};
	}

	const [publications, meta, tags] = await batchApiRequest([
		getPublications({
			filtersQueryParams: params,
			urlParams: params,
			rubricId: rubric?._id,
			tagId: tag?._id,
			withPodcasts
		}),
		getMeta(page, cleanCache),
		getTags({
			rubricPath: rubric?._id,
			tagId: tag?._id,
			withPodcast: withPodcasts
		})
	]);

	const news = await getLatestNews(cleanCache);

	if (
		publications.pagination.total &&
		publications.pagination.total < params.page
	) {
		return {
			redirect: {
				destination: addQuery(`${serverRuntimeConfig.baseUrl}${resolvedUrl}`, {
					page: publications.pagination.total
				}),
				permanent: false
			}
		};
	}

	const urlParams = {
		..._(params).omit('limit', 'pathParams'),
		rubric: rubricPath || null,
		tag: tagName || null,
		withPodcasts: Boolean(withPodcasts)
	};

	return {
		props: {
			publications,
			news: !_.isEmpty(news?.items) ? news.items : null,
			meta,
			urlParams,
			filtersValues: {
				rubricId: rubric?._id || null,
				tagId: tag?._id || null
			},
			tags,
			defaultFiltersValues: {
				sort: defaultSort
			}
		}
	};
}

export default PublicationsPage;
